// Botao global "Retirar fundo" — recorta o fundo de uma foto de produto (gera o
// master transparente da Biblioteca de Fotos, DA-072) e abre um preview
// comparando a foto atual com a versao sem fundo, com swatches de fundo pra
// conferir o recorte + o veredito da regua de qualidade do master (DA-073).
//
// **A IA roda no navegador do usuario** (Web Worker + onnxruntime-web + modelo
// ISNet) — nao no servidor. Fluxo: baixa a foto -> roda a IA local -> mostra
// preview. Se aprovar, o PNG transparente sobe pro servidor que so faz o WebP
// final (centralizar + lossless). Se descartar, nada sai da maquina. Poupa
// RAM/CPU do VPS no caminho manual; o upload de foto nova e o batch da Fase 3
// seguem rodando o rembg no servidor.
//
// Quando o master nao atinge a resolucao do padrao, oferece "Melhorar
// resolucao" — amplia a foto por IA no navegador; o resultado fica em revisao
// e so e salvo quando o usuario aprovar (Fase 4, DA-071).
//
// Reusavel: usado na aba Acervo da Gestao de Fotos, no modal de foto do produto,
// e em qualquer tela que mexa com foto de produto. Centralizar aqui garante que
// melhorias no recorte/preview/avaliacao chegam a todos os pontos de uma vez.
//
// Tres variantes de gatilho:
//   - 'icone'      — so o icone de tesoura (linhas de acoes apertadas)
//   - 'completo'   — icone + rotulo "Retirar fundo" (botao de barra de acoes)
//   - 'controlado' — sem gatilho proprio; abre sozinho ao montar. Usado quando a
//                    acao vive num menu "..." — o menu monta este componente.
import { useState, useEffect, type CSSProperties } from 'react';
import { Scissors, Check, X, ShieldCheck, AlertTriangle, Wand2 } from 'lucide-react';
import { api } from '../lib/api';
import { ampliarFotoParaMaster } from '../lib/upscale-foto';
import { removerFundoNoNavegador } from '../lib/remover-fundo-navegador';
import { useDialog } from './DialogProvider';
import { Modal } from './Modal';

// Fundo de preview do master transparente — pra conferir a qualidade do recorte.
type FundoPreview = 'xadrez' | 'branco' | 'escuro' | 'laranja';

function estiloFundoPreview(fundo: FundoPreview): CSSProperties {
  switch (fundo) {
    case 'branco':
      return { background: '#ffffff' };
    case 'escuro':
      return { background: '#1e2a52' };
    case 'laranja':
      return { background: '#b45309' };
    case 'xadrez':
    default:
      return {
        backgroundColor: '#ffffff',
        backgroundImage:
          'linear-gradient(45deg, #d1d5db 25%, transparent 25%), linear-gradient(-45deg, #d1d5db 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #d1d5db 75%), linear-gradient(-45deg, transparent 75%, #d1d5db 75%)',
        backgroundSize: '18px 18px',
        backgroundPosition: '0 0, 0 9px, 9px -9px, -9px 0px',
      };
  }
}

// Veredito da regua de qualidade do master (espelha AnaliseMaster do backend).
interface AvaliacaoMaster {
  veredito: 'completo' | 'parcial';
  largura: number;
  altura: number;
  alta_resolucao: boolean;
  quadrado: boolean;
  transparente: boolean;
  problemas: string[];
}

// Linha de criterio do checklist de qualidade do master.
function LinhaCriterio({ ok, texto, detalhe }: { ok: boolean; texto: string; detalhe?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
      {ok ? (
        <Check size={16} style={{ color: '#16a34a', flexShrink: 0 }} />
      ) : (
        <X size={16} style={{ color: '#6b7280', flexShrink: 0 }} />
      )}
      <span style={{ fontWeight: 600, color: 'var(--text-color)' }}>{texto}</span>
      {detalhe && (
        <span style={{ color: 'var(--text-muted, #6b7280)', fontSize: 12 }}>{detalhe}</span>
      )}
    </div>
  );
}

// Painel com o veredito da regua de qualidade do master + os 3 criterios.
// Quando falta resolucao, oferece o botao "Melhorar resolucao" (upscale por IA).
function PainelVeredito({
  a,
  onMelhorar,
  melhorando,
  progresso,
}: {
  a: AvaliacaoMaster;
  onMelhorar?: () => void;
  melhorando?: boolean;
  progresso?: number;
}) {
  const completo = a.veredito === 'completo';
  return (
    <div
      style={{
        marginTop: 14,
        border: '1px solid var(--panel-border, #e5e7eb)',
        borderRadius: 8,
        overflow: 'hidden',
      }}
    >
      {/* Selo do veredito */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 12px',
          background: completo ? '#dcfce7' : '#fde68a',
          color: completo ? '#15803d' : '#000000',
          fontWeight: 800,
          fontSize: 14,
          letterSpacing: 0.5,
        }}
      >
        {completo ? <ShieldCheck size={18} /> : <AlertTriangle size={18} />}
        {completo ? 'MASTER COMPLETO' : 'MASTER PARCIAL'}
      </div>
      {/* Checklist dos criterios */}
      <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 7 }}>
        <LinhaCriterio ok={a.transparente} texto="Fundo transparente" />
        <LinhaCriterio
          ok={a.alta_resolucao}
          texto="Resolução ≥ 2000px"
          detalhe={a.largura > 0 ? `${a.largura}×${a.altura}px` : undefined}
        />
        <div style={{ fontSize: 12, color: 'var(--text-muted, #6b7280)', marginTop: 2 }}>
          {completo
            ? 'Este recorte atende todos os critérios do master.'
            : 'O recorte já serve pra flys e Reels. Para virar master completo, os itens marcados com ✗ ainda precisam ser atingidos.'}
        </div>

        {/* Falta resolucao — oferece ampliar por IA no navegador. */}
        {!a.alta_resolucao && onMelhorar && (
          <div style={{ marginTop: 4 }}>
            <button
              type="button"
              onClick={onMelhorar}
              disabled={melhorando}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold"
              style={{
                background: '#ede9fe',
                color: '#6d28d9',
                cursor: melhorando ? 'wait' : 'pointer',
              }}
            >
              <Wand2 size={16} className={melhorando ? 'animate-pulse' : undefined} />
              {melhorando ? 'Ampliando no navegador...' : 'Melhorar resolução com IA'}
            </button>
            {melhorando && (
              <div style={{ marginTop: 8 }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 12,
                    color: 'var(--text-muted, #6b7280)',
                    marginBottom: 4,
                  }}
                >
                  <span>Ampliando a foto no seu computador...</span>
                  <span style={{ fontWeight: 700 }}>{Math.round((progresso ?? 0) * 100)}%</span>
                </div>
                <div
                  style={{
                    height: 8,
                    borderRadius: 999,
                    background: 'var(--panel-border, #e5e7eb)',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${Math.round((progresso ?? 0) * 100)}%`,
                      background: '#7c3aed',
                      transition: 'width 0.25s ease',
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Estado de uma melhoria por IA pendente de aprovacao.
interface Revisao {
  staging: string;
  previewAnterior: string | null;
  avaliacaoAnterior: AvaliacaoMaster | null;
}

// Recorte feito no navegador, aguardando o usuario aprovar antes de subir.
interface RecorteCliente {
  blob: Blob;
  objectUrl: string;
}

// Mensagens de etapa do worker da IA local, traduzidas pra UX amigavel.
function rotuloEtapa(etapa: string): string {
  switch (etapa) {
    case 'baixando-modelo':
      return 'Baixando modelo de IA (uma vez só)...';
    case 'baixando-foto':
      return 'Baixando a foto...';
    case 'preparando':
      return 'Preparando a imagem...';
    case 'inferindo':
      return 'Recortando o fundo...';
    case 'aplicando-mascara':
      return 'Aplicando o recorte...';
    case 'finalizando':
      return 'Finalizando...';
    default:
      return 'Processando...';
  }
}

interface Props {
  // Foto cujo fundo sera recortado (ex: /uploads/produtos/x.webp).
  fotoUrl: string;
  // Master ja existente, se conhecido — pula a geracao e abre o preview direto.
  masterUrl?: string | null;
  // Avisa o pai quando um master e gerado/aprovado (pra atualizar badge/estado).
  // completo indica se o master atende a regua de qualidade completa (DA-073).
  onGerado?: (masterUrl: string, completo: boolean) => void;
  // Aparencia do gatilho. 'controlado' = sem gatilho proprio, abre ao montar.
  // Default 'completo'.
  variante?: 'icone' | 'completo' | 'controlado';
  disabled?: boolean;
  // No modo 'controlado', avisado quando o fluxo termina (modal fechado ou erro).
  onFecharControle?: () => void;
}

export function BotaoRetirarFundo({
  fotoUrl,
  masterUrl,
  onGerado,
  variante = 'completo',
  disabled,
  onFecharControle,
}: Props) {
  const dialog = useDialog();
  // Estado da IA local (recortando o fundo no navegador).
  const [gerando, setGerando] = useState(false);
  const [progressoIA, setProgressoIA] = useState<{ etapa: string; valor: number } | null>(null);
  // Recorte pendente de aprovacao (objectURL local — so vai pro servidor se aprovar).
  const [recorte, setRecorte] = useState<RecorteCliente | null>(null);
  const [aprovandoRecorte, setAprovandoRecorte] = useState(false);
  // masterUrl exibido no modal de preview (null = modal fechado).
  const [preview, setPreview] = useState<string | null>(null);
  const [fundo, setFundo] = useState<FundoPreview>('xadrez');
  // Veredito da regua de qualidade do master (so apos salvar).
  const [avaliacao, setAvaliacao] = useState<AvaliacaoMaster | null>(null);
  const [avaliando, setAvaliando] = useState(false);
  // Ampliacao por IA em andamento + progresso (0 a 1).
  const [melhorando, setMelhorando] = useState(false);
  const [progresso, setProgresso] = useState(0);
  // Melhoria por IA pendente de aprovacao (staging).
  const [revisao, setRevisao] = useState<Revisao | null>(null);
  const [aprovando, setAprovando] = useState(false);

  const temMaster = !!masterUrl;

  async function acionar() {
    // Ja tem master — abre o preview do master oficial direto e busca o veredito.
    if (masterUrl) {
      setFundo('xadrez');
      setAvaliacao(null);
      setPreview(masterUrl);
      setAvaliando(true);
      try {
        const r = await api.post<{ avaliacao: AvaliacaoMaster | null }>(
          '/api/produtos/foto-avaliar-master',
          { foto_url: fotoUrl },
        );
        setAvaliacao(r.avaliacao);
      } catch {
        /* avaliacao e opcional — o preview ja abriu */
      } finally {
        setAvaliando(false);
      }
      return;
    }
    // Sem master — roda a IA no navegador. Resultado fica pendente ate aprovar.
    setGerando(true);
    setProgressoIA({ etapa: 'baixando-modelo', valor: 0 });
    try {
      const blob = await removerFundoNoNavegador(fotoUrl, (p) => setProgressoIA(p));
      const objectUrl = URL.createObjectURL(blob);
      setRecorte({ blob, objectUrl });
      setFundo('xadrez');
      setAvaliacao(null);
      setPreview(objectUrl);
    } catch (e: any) {
      dialog.toast(e.message ?? 'Falha ao retirar o fundo da foto', { tipo: 'erro' });
      onFecharControle?.();
    } finally {
      setGerando(false);
      setProgressoIA(null);
    }
  }

  // Reprocessa: roda a IA no navegador de novo, mesmo ja tendo master. O recorte
  // novo fica pendente de aprovacao; se aprovado, substitui o master atual.
  async function reprocessar() {
    setPreview(null); // fecha o preview do master atual — so a barra de progresso aparece
    setGerando(true);
    setProgressoIA({ etapa: 'baixando-modelo', valor: 0 });
    try {
      const blob = await removerFundoNoNavegador(fotoUrl, (p) => setProgressoIA(p));
      const objectUrl = URL.createObjectURL(blob);
      setRecorte({ blob, objectUrl });
      setFundo('xadrez');
      setAvaliacao(null);
      setPreview(objectUrl);
    } catch (e: any) {
      dialog.toast(e.message ?? 'Falha ao reprocessar o recorte', { tipo: 'erro' });
      onFecharControle?.();
    } finally {
      setGerando(false);
      setProgressoIA(null);
    }
  }

  // No modo controlado o componente nao tem gatilho — aciona sozinho ao montar.
  useEffect(() => {
    if (variante === 'controlado') acionar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Aprova o recorte feito no navegador — sobe o PNG transparente pro servidor,
  // que so faz o WebP master final. Sem staging — o usuario ja aprovou no preview.
  async function aprovarRecorte() {
    if (!recorte) return;
    setAprovandoRecorte(true);
    try {
      const fd = new FormData();
      fd.append('foto_url', fotoUrl);
      fd.append('imagem', recorte.blob, 'recorte.png');
      const token = localStorage.getItem('fiodecobre.token');
      const resp = await fetch('/api/upload/master-cliente', {
        method: 'POST',
        body: fd,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) {
        const d = await resp.json().catch(() => ({}));
        throw new Error((d as any).erro || `HTTP ${resp.status}`);
      }
      const r = (await resp.json()) as {
        master_url: string;
        avaliacao: AvaliacaoMaster | null;
      };
      // Libera o objectURL local — agora vamos exibir o master oficial.
      URL.revokeObjectURL(recorte.objectUrl);
      setRecorte(null);
      setPreview(`${r.master_url}?v=${Date.now()}`);
      setAvaliacao(r.avaliacao);
      onGerado?.(r.master_url, r.avaliacao?.veredito === 'completo');
      dialog.toast('Master aprovado e salvo', { tipo: 'sucesso' });
    } catch (e: any) {
      // Falha de salvamento e dor de cabeca (caso 0807 2026-05-19): o usuario
      // aprovou na UI achando que persistiu, mas o banco nao recebeu. Em vez
      // de toast (que some), abrimos alerta bloqueante e MANTEMOS o recorte
      // pra o usuario poder tentar de novo sem precisar repetir o passo da IA.
      console.error('[aprovarRecorte] falhou', e);
      await dialog.alerta(
        `Nao consegui salvar o Master no servidor.\n\nMotivo: ${e?.message ?? 'erro desconhecido'}\n\nO recorte continua aqui — clique em "Aprovar e salvar" de novo. Se insistir, me avise.`,
        { titulo: 'Falha ao salvar Master', tipo: 'erro' },
      );
    } finally {
      setAprovandoRecorte(false);
    }
  }

  // Descarta o recorte local — nada sobe pro servidor.
  function descartarRecorte() {
    if (!recorte) return;
    URL.revokeObjectURL(recorte.objectUrl);
    setRecorte(null);
    setPreview(null);
    onFecharControle?.();
  }

  // Amplia a foto por IA (no navegador). O resultado fica em revisao — so vira
  // master oficial quando o usuario aprovar.
  async function melhorarResolucao() {
    setMelhorando(true);
    setProgresso(0);
    try {
      const blob = await ampliarFotoParaMaster(fotoUrl, (r) => setProgresso(r));
      const fd = new FormData();
      fd.append('foto_url', fotoUrl);
      fd.append('imagem', blob, 'ampliada.webp');
      const token = localStorage.getItem('fiodecobre.token');
      const resp = await fetch('/api/upload/melhorar-master', {
        method: 'POST',
        body: fd,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) {
        const d = await resp.json().catch(() => ({}));
        throw new Error((d as any).erro || 'falha ao melhorar a resolução');
      }
      const r = (await resp.json()) as {
        staging: string;
        preview_url: string;
        avaliacao: AvaliacaoMaster | null;
      };
      setRevisao({ staging: r.staging, previewAnterior: preview, avaliacaoAnterior: avaliacao });
      setFundo('xadrez');
      setPreview(r.preview_url);
      setAvaliacao(r.avaliacao);
    } catch (e: any) {
      dialog.toast(e.message ?? 'Falha ao melhorar a resolução', { tipo: 'erro' });
    } finally {
      setMelhorando(false);
    }
  }

  // Aprova a melhoria pendente — o staging vira o master oficial.
  async function aprovarMaster() {
    if (!revisao) return;
    // Estado inconsistente (ex: depois de varias substituicoes a referencia se perde):
    // avisa claro em vez de falhar calado.
    if (!revisao.staging || !fotoUrl) {
      await dialog.alerta(
        'Perdi a referencia do recorte (estado inconsistente). Feche este modal, de um F5 e refaca o "Melhorar resolucao" numa tacada so.',
        { titulo: 'Nao consegui aprovar o Master', tipo: 'erro' },
      );
      return;
    }
    setAprovando(true);
    try {
      const r = await api.post<{ master_url: string }>('/api/upload/master-aprovar', {
        foto_url: fotoUrl,
        staging: revisao.staging,
      });
      onGerado?.(r.master_url, avaliacao?.veredito === 'completo');
      setRevisao(null);
      setPreview(`${r.master_url}?v=${Date.now()}`);
      dialog.toast('Master aprovado e salvo', { tipo: 'sucesso' });
    } catch (e: any) {
      // Alerta bloqueante (nao toast que some) com o motivo, e MANTEM a revisao
      // pra tentar de novo sem refazer a melhoria. Antes era toast e o usuario
      // nao via por que "nao dava".
      console.error('[aprovarMaster] falhou', e);
      await dialog.alerta(
        `Nao consegui salvar o Master no servidor.\n\nMotivo: ${e?.message ?? 'erro desconhecido'}\n\nO resultado continua aqui — clique em "Aprovar e salvar" de novo. Se insistir, me avise o motivo que apareceu.`,
        { titulo: 'Falha ao aprovar o Master', tipo: 'erro' },
      );
    } finally {
      setAprovando(false);
    }
  }

  // Descarta a melhoria pendente — volta ao master anterior.
  async function descartarMaster() {
    if (!revisao) return;
    setAprovando(true);
    try {
      await api.post('/api/upload/master-descartar', { staging: revisao.staging });
    } catch {
      /* segue — o staging vira lixo, mas o master anterior esta intacto */
    }
    setPreview(revisao.previewAnterior);
    setAvaliacao(revisao.avaliacaoAnterior);
    setRevisao(null);
    setAprovando(false);
    dialog.toast('Melhoria descartada — o master anterior foi mantido');
  }

  // Fecha o modal. Se ha melhoria pendente, descarta o staging pra nao deixar lixo.
  // Se ha recorte local pendente, libera o objectURL.
  function fecharModal() {
    if (melhorando || aprovando || aprovandoRecorte) return;
    if (revisao) {
      api.post('/api/upload/master-descartar', { staging: revisao.staging }).catch(() => {});
      setRevisao(null);
    }
    if (recorte) {
      URL.revokeObjectURL(recorte.objectUrl);
      setRecorte(null);
    }
    setPreview(null);
    onFecharControle?.();
  }

  const titulo = temMaster
    ? 'Ver esta foto com o fundo recortado'
    : 'Retirar o fundo desta foto (gera a versao transparente)';

  return (
    <>
      {variante === 'icone' && (
        <button
          type="button"
          onClick={acionar}
          disabled={disabled || gerando}
          title={titulo}
          className={gerando ? 'animate-pulse' : undefined}
          style={{
            background: 'transparent',
            border: 'none',
            color: temMaster ? '#16a34a' : '#9333ea',
            cursor: gerando ? 'wait' : 'pointer',
            padding: 2,
            flexShrink: 0,
          }}
        >
          <Scissors size={14} />
        </button>
      )}
      {variante === 'completo' && (
        <button
          type="button"
          onClick={acionar}
          disabled={disabled || gerando}
          title={titulo}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium"
          style={{
            background: temMaster ? '#dcfce7' : '#f3e8ff',
            color: temMaster ? '#15803d' : '#7e22ce',
            cursor: gerando ? 'wait' : 'pointer',
          }}
        >
          <Scissors size={16} />
          {gerando ? 'Retirando fundo...' : temMaster ? 'Ver sem fundo' : 'Retirar fundo'}
        </button>
      )}

      {/* Feedback enquanto a IA roda no navegador (barra de progresso por etapa). */}
      {gerando && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1250,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              background: 'var(--modal-bg, var(--panel-bg, #fff))',
              color: 'var(--text-color)',
              padding: '20px 28px',
              borderRadius: 10,
              minWidth: 320,
              boxShadow: '0 8px 30px rgba(0,0,0,0.25)',
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>
              Recortando o fundo no seu computador...
            </div>
            <div
              style={{
                fontSize: 12,
                color: 'var(--text-muted, #6b7280)',
                marginBottom: 8,
              }}
            >
              {rotuloEtapa(progressoIA?.etapa || '')}
            </div>
            <div
              style={{
                height: 8,
                borderRadius: 999,
                background: 'var(--panel-border, #e5e7eb)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${Math.round((progressoIA?.valor ?? 0) * 100)}%`,
                  background: '#9333ea',
                  transition: 'width 0.25s ease',
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Preview: foto atual x foto sem fundo + veredito da regua do master. */}
      {preview && (
        <Modal
          aberto
          onFechar={fecharModal}
          titulo={
            recorte
              ? 'Conferir recorte'
              : revisao
                ? 'Revisar melhoria da foto'
                : 'Foto sem fundo'
          }
          largura={640}
          rodape={
            recorte ? (
              <>
                <button
                  type="button"
                  className="btn-secundario"
                  onClick={descartarRecorte}
                  disabled={aprovandoRecorte}
                >
                  Descartar
                </button>
                <button
                  type="button"
                  className="btn-primario"
                  onClick={aprovarRecorte}
                  disabled={aprovandoRecorte}
                >
                  {aprovandoRecorte ? 'Salvando...' : 'Aprovar e salvar'}
                </button>
              </>
            ) : revisao ? (
              <>
                <button
                  type="button"
                  className="btn-secundario"
                  onClick={descartarMaster}
                  disabled={aprovando}
                >
                  Descartar
                </button>
                <button
                  type="button"
                  className="btn-primario"
                  onClick={aprovarMaster}
                  disabled={aprovando}
                >
                  {aprovando ? 'Salvando...' : 'Aprovar e salvar'}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="btn-secundario"
                  onClick={reprocessar}
                  disabled={gerando || melhorando}
                  title="Refaz o recorte com a IA do navegador (o resultado fica pra voce aprovar)"
                >
                  {gerando ? 'Reprocessando...' : 'Reprocessar'}
                </button>
                <button
                  type="button"
                  className="btn-secundario"
                  onClick={fecharModal}
                  disabled={melhorando || gerando}
                >
                  Fechar
                </button>
              </>
            )
          }
        >
          {recorte ? (
            <div
              style={{
                fontSize: 13,
                marginBottom: 12,
                padding: '8px 12px',
                borderRadius: 8,
                background: '#fef9c3',
                color: '#000000',
                fontWeight: 600,
              }}
            >
              Confira o recorte abaixo. Clique <strong>Aprovar e salvar</strong> pra subir o
              resultado, ou <strong>Descartar</strong> pra jogar fora — nada sai do seu
              computador até voce aprovar.
            </div>
          ) : revisao ? (
            <div
              style={{
                fontSize: 13,
                marginBottom: 12,
                padding: '8px 12px',
                borderRadius: 8,
                background: '#fef9c3',
                color: '#000000',
                fontWeight: 600,
              }}
            >
              Confira o resultado da IA abaixo. Clique <strong>Aprovar e salvar</strong> pra
              guardar como master, ou <strong>Descartar</strong> pra manter o master anterior.
            </div>
          ) : (
            <p style={{ fontSize: 13, color: 'var(--text-muted, #6b7280)', marginBottom: 10 }}>
              Fundo recortado e guardado como <strong>foto master transparente</strong> — usada
              nos flys, Reels e anuncios. As telas internas seguem com a foto normal.
            </p>
          )}

          {/* Swatches: conferir o recorte sobre fundos diferentes. */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
            {(
              [
                ['xadrez', 'Transparencia'],
                ['branco', 'Branco'],
                ['escuro', 'Escuro'],
                ['laranja', 'Laranja'],
              ] as const
            ).map(([id, rot]) => (
              <button
                key={id}
                type="button"
                onClick={() => setFundo(id)}
                style={{
                  flex: 1,
                  padding: '5px 4px',
                  fontSize: 12,
                  fontWeight: 700,
                  borderRadius: 6,
                  cursor: 'pointer',
                  border: `2px solid ${
                    fundo === id ? 'var(--accent)' : 'var(--panel-border, #e5e7eb)'
                  }`,
                  background: fundo === id ? 'var(--accent)' : 'var(--input-bg, #fff)',
                  color: fundo === id ? 'var(--accent-fg)' : 'var(--text-color)',
                }}
              >
                {rot}
              </button>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <div style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>Foto atual</div>
              <img
                src={fotoUrl}
                alt="Foto atual"
                style={{
                  width: '100%',
                  height: 196,
                  objectFit: 'contain',
                  background: '#fff',
                  borderRadius: 6,
                  border: '1px solid var(--panel-border, #e5e7eb)',
                }}
              />
            </div>
            <div style={{ fontSize: 24, color: 'var(--text-muted, #9ca3af)', flexShrink: 0 }}>→</div>
            <div style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>
                {recorte ? 'Recorte (no seu PC)' : revisao ? 'Resultado da IA' : 'Sem fundo'}
              </div>
              <img
                src={preview}
                alt="Foto sem fundo"
                style={{
                  width: '100%',
                  height: 196,
                  objectFit: 'contain',
                  borderRadius: 6,
                  border: '1px solid var(--panel-border, #e5e7eb)',
                  ...estiloFundoPreview(fundo),
                }}
              />
            </div>
          </div>

          {/* Veredito da regua de qualidade do master — so para master JA salvo. */}
          {!recorte && avaliando && (
            <div
              style={{
                marginTop: 14,
                fontSize: 13,
                color: 'var(--text-muted, #6b7280)',
                textAlign: 'center',
              }}
            >
              Avaliando a qualidade do master...
            </div>
          )}
          {!recorte && !avaliando && avaliacao && (
            <PainelVeredito
              a={avaliacao}
              onMelhorar={revisao ? undefined : melhorarResolucao}
              melhorando={melhorando}
              progresso={progresso}
            />
          )}
        </Modal>
      )}
    </>
  );
}
